import { createReducer } from '@reduxjs/toolkit';
import { DataType } from '@vgtv/api-client/lib/component';

import { fetchSvpAssets } from '../assets';
import { fetchSvpCategories } from '../categories';
import {
  fetchPageByCategoryId,
  fetchPageBySlug,
  fetchPageByStoryId,
  fetchPageByTagId,
} from '../pages';
import { fetchProfiles } from '../profiles';
import { fetchSearchResults } from '../search/actions';

import { getPageComponentsWithPresetIds } from './helpers';

import type { Profile } from '@vgtv/api-client/lib/cnp_tag';
import type { SvpCategoryId } from '@vgtv/api-client/lib/svp_category';
import type { SvpAssetId } from '@vgtv/api-client/lib/svp_asset';
import type {
  ComponentId,
  ComponentType,
} from '@vgtv/api-client/lib/component';

export * from './selectors';

type ComponentsStateEntry = {
  type: ComponentType;
  dataUrl?: string;
  nextPageParam?: string;
} & (
  | {
      dataType: DataType.ASSETS;
      dataIds: SvpAssetId[];
    }
  | {
      dataType: DataType.STORIES;
      dataIds: SvpAssetId[];
    }
  | {
      dataType: DataType.CATEGORIES;
      dataIds: SvpCategoryId[];
    }
  | {
      dataType: DataType.PROFILES;
      dataIds: Profile['id'][];
    }
  | {
      dataType: DataType.MIXED;
      dataIds: (`asset:${SvpAssetId}` | `category:${SvpCategoryId}`)[];
    }
);

export type ComponentsState = Partial<Record<string, ComponentsStateEntry>>;

function storePageComponentsWithPreset(
  state: ComponentsState,
  action: Pick<
    ReturnType<
      | typeof fetchPageByCategoryId.fulfilled
      | typeof fetchPageByTagId.fulfilled
      | typeof fetchPageByStoryId.fulfilled
      | typeof fetchPageBySlug.fulfilled
    >,
    'type' | 'payload'
  >
) {
  if (action.payload) {
    const pageConfig = action.payload;

    const components = getPageComponentsWithPresetIds(pageConfig);

    components.forEach(({ component, dataIds }) => {
      const { id: componentId, type, data } = component;

      state[componentId] = {
        type,
        dataType: data?.type,
        dataIds,
      } as ComponentsStateEntry;
    });
  }
}

const initialState: ComponentsState = {};

const componentsReducer = createReducer(initialState, (builder) =>
  builder
    .addCase(fetchSvpAssets.fulfilled, (state, action) => {
      const {
        _embedded: { assets },
        _links: { next: { href: nextPageHref = undefined } = {} },
      } = action.payload;
      const { dataUrl, page } = action.meta.arg;

      // FIXME: no idea where these come from. API/Admin maybe?
      const { componentId, type } = action.meta.arg as {
        componentId?: string;
        type?: ComponentsStateEntry['type'];
      };

      const dataIds = assets
        .map((asset) => asset.id)
        .filter((assetId) => {
          return !(
            // Filter out 24/7 live news feed
            (componentId === '5fd2069370b31c0018ab6bf3' && assetId === 149692)
          );
        });

      const nextPageMatch = nextPageHref
        ? nextPageHref.match(/page=([0-9]+)/)
        : undefined;

      const nextPageParam = nextPageMatch ? nextPageMatch[1] : undefined;

      if (componentId) {
        const prevState = state[componentId];
        if (page !== undefined && prevState) {
          state[componentId] = {
            ...prevState,
            dataType: DataType.ASSETS,
            dataIds: [...(prevState.dataIds as typeof dataIds), ...dataIds],
            nextPageParam,
          };
        } else {
          state[componentId] = {
            type: type!,
            dataType: DataType.ASSETS,
            dataUrl,
            dataIds,
            nextPageParam,
          };
        }
      }
    })
    .addCase(fetchSvpCategories.fulfilled, (state, action) => {
      const {
        _embedded: { categories },
      } = action.payload;

      const dataIds = categories.map((category) => category.id);
      const { dataUrl } = action.meta.arg;
      const { componentId, type } = action.meta.arg as {
        componentId?: string;
        type?: ComponentType;
      };

      if (componentId) {
        state[componentId] = {
          type: type!,
          dataType: DataType.CATEGORIES,
          dataUrl,
          dataIds,
        };
      }
    })
    .addCase(fetchProfiles.fulfilled, (state, action) => {
      const profiles = action.payload;

      const dataIds = profiles.map((profile) => profile.id);

      const { dataUrl } = action.meta.arg;
      const { componentId, type } = action.meta.arg as {
        componentId?: ComponentId;
        type?: ComponentType;
      };

      if (componentId) {
        state[componentId] = {
          type: type!,
          dataType: DataType.PROFILES,
          dataUrl,
          dataIds,
        };
      }
    })
    .addCase(fetchPageByCategoryId.fulfilled, storePageComponentsWithPreset)
    .addCase(fetchPageByTagId.fulfilled, storePageComponentsWithPreset)
    .addCase(fetchPageByStoryId.fulfilled, storePageComponentsWithPreset)
    .addCase(fetchPageBySlug.fulfilled, storePageComponentsWithPreset)
    .addCase(fetchSearchResults.fulfilled, (state, action) => {
      const { components } = action.payload;

      components.forEach((component) => {
        state[component.id] = {
          type: component.type,
          dataType: component.data.type,
          dataIds: component.data.preset.map((item) => item.id),
        } as ComponentsStateEntry;
      });
    })
);

export { componentsReducer, storePageComponentsWithPreset };
